import React, { useState } from 'react';
import { Gift, Search, CreditCard, Check, X, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { giftcardApi } from '../../../services/giftcardApi';

export default function GiftCardRedeemModal({ open, onClose, orderTotal, orderId, onApply }) {
  const [step, setStep] = useState('input'); // input | balance | processing | success | error
  const [code, setCode] = useState('');
  const [card, setCard] = useState(null);
  const [applyAmount, setApplyAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleLookup = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await giftcardApi.checkBalance({
        code: code.trim(),
      });
      setCard(res);
      setApplyAmount(Math.min(res.current_balance, orderTotal).toFixed(2));
      setStep('balance');
    } catch (e) {
      setError(e.message);
      setStep('error');
    }
    setLoading(false);
  };

  const handleRedeem = async () => {
    const amount = parseFloat(applyAmount);
    if (!amount || amount <= 0) return;
    setLoading(true);
    try {
      const res = await giftcardApi.redeem({
        code: code.trim(),
        requested_amount: amount,
        order_id: orderId,
        source_channel: 'restaurant_pos',
        idempotency_key: `pos-redeem-${orderId}-${Date.now()}`,
      });
      setResult(res);
      setStep('success');
      toast.success(`Gift card applied: $${res.applied_amount}`);
      onApply?.({
        applied_amount: res.applied_amount,
        remaining_balance: res.remaining_balance,
        split_tender_remaining: res.split_tender_remaining,
        gift_card_status: res.gift_card_status,
        card_code: code.trim(),
      });
    } catch (e) {
      setError(e.message);
      setStep('error');
    }
    setLoading(false);
  };

  const reset = () => { setStep('input'); setCode(''); setCard(null); setApplyAmount(''); setResult(null); setError(''); };

  const maskCode = (c) => c ? `****-${c.slice(-4)}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="gc-redeem-modal">
      <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-md border border-white/10">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            <span className="text-white font-semibold">Redeem Gift Card</span>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-gray-500 hover:text-white" data-testid="gc-redeem-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4">
          {step === 'input' && (
            <div data-testid="gc-redeem-step-input">
              <p className="text-gray-400 text-sm mb-1">Order Total: <span className="text-white font-bold">${orderTotal?.toFixed(2)}</span></p>
              <p className="text-gray-500 text-xs mb-4">Enter gift card code or scan QR</p>
              <div className="flex gap-2">
                <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                  placeholder="GC-XXXX-XXXX-XXXX" autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono placeholder-gray-600 outline-none focus:border-purple-400/50"
                  data-testid="gc-redeem-code-input" />
                <Button onClick={handleLookup} disabled={loading || !code.trim()}
                  className="bg-purple-500 hover:bg-purple-600 px-4" data-testid="gc-redeem-lookup">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          )}

          {step === 'balance' && card && (
            <div data-testid="gc-redeem-step-balance">
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm">Card Balance</span>
                  <span className={`text-2xl font-bold ${card.current_balance > 0 ? 'text-emerald-400' : 'text-red-400'}`}>${card.current_balance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Original: ${card.original_amount}</span>
                  <span className="text-gray-500 capitalize">{card.status.replace('_', ' ')}</span>
                </div>
                {card.recipient_name && <p className="text-gray-400 text-xs mt-1">For: {card.recipient_name}</p>}
                {card.is_expired && <p className="text-red-400 text-xs mt-1">This card has expired</p>}
              </div>

              {card.current_balance > 0 && !card.is_expired && (
                <>
                  <p className="text-gray-400 text-sm mb-2">Amount to apply</p>
                  <div className="relative mb-4">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <input type="number" value={applyAmount} onChange={e => setApplyAmount(e.target.value)}
                      max={Math.min(card.current_balance, orderTotal)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-4 py-3 text-white text-xl font-bold outline-none focus:border-purple-400/50"
                      data-testid="gc-redeem-amount" />
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 mb-4 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-gray-400">Order Total</span><span className="text-white">${orderTotal?.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Gift Card</span><span className="text-purple-400">-${parseFloat(applyAmount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between border-t border-white/5 pt-1">
                      <span className="text-gray-400">Remaining</span>
                      <span className="text-white font-bold">${Math.max(0, orderTotal - parseFloat(applyAmount || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                  <Button onClick={handleRedeem} disabled={loading || !applyAmount || parseFloat(applyAmount) <= 0}
                    className="w-full bg-purple-500 hover:bg-purple-600 font-semibold h-12" data-testid="gc-redeem-apply">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Apply $${parseFloat(applyAmount || 0).toFixed(2)}`}
                  </Button>
                </>
              )}
              {(card.current_balance <= 0 || card.is_expired) && (
                <Button onClick={reset} className="w-full bg-white/10 hover:bg-white/20 text-white">Try Another Card</Button>
              )}
            </div>
          )}

          {step === 'success' && result && (
            <div className="text-center py-4" data-testid="gc-redeem-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Gift Card Applied!</h3>
              <p className="text-purple-400 text-2xl font-bold mb-3">-${result.applied_amount.toFixed(2)}</p>
              <div className="bg-white/5 rounded-xl p-3 mb-4 text-left space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Card</span><span className="text-white font-mono">{maskCode(code)}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Card Balance</span><span className="text-white">${result.remaining_balance.toFixed(2)}</span></div>
                {result.split_tender_remaining > 0 && (
                  <div className="flex justify-between"><span className="text-gray-400">Still Due</span><span className="text-amber-400 font-bold">${result.split_tender_remaining.toFixed(2)}</span></div>
                )}
              </div>
              <Button onClick={() => { reset(); onClose(); }} className="w-full bg-white/10 hover:bg-white/20 text-white" data-testid="gc-redeem-done">Done</Button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-4" data-testid="gc-redeem-error">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Unable to Process</h3>
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <Button onClick={reset} className="w-full bg-white/10 hover:bg-white/20 text-white">Try Again</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
